/**
 * Minimal layer switcher — shown when full Layer View panel is hidden.
 */
import React, { useState, useEffect } from 'react';
import { LayerManager } from '../../services/layerManager';
import { UndergroundLayer } from '../../types/underground';
import { playUiSfx } from '../../utils/uiAudio';
import { resolveSwitchUndergroundLayer } from '../../src/adapters/ui/hooks/useLayerSwitch';
import { surfaces, buttons, cn } from './kit';

interface CompactLayerStripProps {
  layerManager: LayerManager;
}

const LAYERS: { id: UndergroundLayer; label: string; icon: string; testId: string }[] = [
  { id: UndergroundLayer.Surface, label: 'Surface', icon: '🏢', testId: 'surface' },
  { id: UndergroundLayer.Infrastructure, label: 'Infra', icon: '⚙️', testId: 'infrastructure' },
  { id: UndergroundLayer.Connectivity, label: 'Network', icon: '🔌', testId: 'connectivity' },
];

const CompactLayerStrip: React.FC<CompactLayerStripProps> = ({ layerManager }) => {
  const [active, setActive] = useState(layerManager.getCurrentState().currentLayer);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    return layerManager.subscribe((s) => {
      setActive(s.currentLayer);
      setTransitioning(s.isTransitioning);
    });
  }, [layerManager]);

  return (
    <div
      className={cn(surfaces.hud, 'flex items-center gap-0.5 rounded-lg p-0.5 shadow-none')}
      data-testid="compact-layer-strip"
    >
      {LAYERS.map((layer) => (
        <button
          key={layer.id}
          type="button"
          data-testid={`layer-select-${layer.testId}`}
          disabled={transitioning}
          title={layer.label}
          onClick={() => {
            playUiSfx('click');
            resolveSwitchUndergroundLayer(layer.id, 600);
          }}
          data-active={active === layer.id ? 'true' : 'false'}
          className={cn(
            'px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide transition-all border-b-0',
            active === layer.id
              ? cn(buttons.tabActive, 'shadow-[0_0_8px_rgba(6,182,212,0.4)]')
              : cn(buttons.tabIdle, 'hover:bg-white/10'),
          )}
        >
          <span className="mr-0.5">{layer.icon}</span>
          <span className="hidden sm:inline">{layer.label}</span>
        </button>
      ))}
    </div>
  );
};

export default CompactLayerStrip;